<template>
  <section class="activity-agenda">
    <div class="activity-agenda__summary">
      <div class="agenda-summary-card">
        <span
          class="agenda-summary-card__icon agenda-summary-card__icon--today"
        >
          <fluent-icon icon="clipboard-task" size="32" />
        </span>
        <div>
          <p class="agenda-summary-card__label">
            {{ $t('ACTIVITY_AGENDA.SUMMARY.TODAY') }}
          </p>
          <p class="agenda-summary-card__count" data-summary="today">
            {{ summary.todayCount }}
          </p>
        </div>
      </div>
      <div class="agenda-summary-card">
        <span
          class="agenda-summary-card__icon agenda-summary-card__icon--overdue"
        >
          <fluent-icon icon="list" size="32" />
        </span>
        <div>
          <p class="agenda-summary-card__label">
            {{ $t('ACTIVITY_AGENDA.SUMMARY.OVERDUE') }}
          </p>
          <p class="agenda-summary-card__count" data-summary="overdue">
            {{ summary.overdueCount }}
          </p>
        </div>
      </div>
    </div>

    <p v-if="!canWriteOpportunities" class="activity-agenda__read-only">
      {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.READ_ONLY') }}
    </p>

    <woot-loading-state
      v-if="isLoading"
      :message="$t('ACTIVITY_AGENDA.LOADING')"
    />

    <p v-else-if="hasError" class="activity-agenda__error">
      {{ $t('ACTIVITY_AGENDA.ERRORS.LOAD') }}
    </p>

    <div v-else-if="activities.length" class="activity-agenda__content">
      <ul class="activity-agenda__list">
        <activity-agenda-item
          v-for="activity in activities"
          :key="activity.id"
          :activity="activity"
          :now="now"
          :can-write="canWriteOpportunities"
          :is-busy="busyActivityId === activity.id"
          @complete="completeActivity"
          @reschedule="openRescheduleModal"
          @cancel="cancelActivity"
        />
      </ul>
      <woot-button
        v-if="hasMore"
        variant="clear"
        size="small"
        class="activity-agenda__load-more"
        :is-loading="isLoadingMore"
        @click="loadMore"
      >
        {{ $t('ACTIVITY_AGENDA.LOAD_MORE') }}
      </woot-button>
    </div>

    <div v-else class="activity-agenda__empty">
      <img :src="emptyImage" alt="" class="activity-agenda__empty-image" />
      <p class="activity-agenda__empty-message">
        {{ $t('ACTIVITY_AGENDA.EMPTY') }}
      </p>
    </div>

    <opportunity-activity-modal
      :show="!!reschedulingActivity"
      :activity="reschedulingActivity"
      :is-submitting="isSubmitting"
      @close="closeRescheduleModal"
      @submit="rescheduleActivity"
    />

    <woot-confirm-modal
      ref="cancelConfirmation"
      :title="$t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.TITLE')"
      :description="
        $t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.DESCRIPTION')
      "
      :confirm-label="
        $t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.CONFIRM')
      "
      :cancel-label="$t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.BACK')"
    />
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { debounce } from '@chatwoot/utils';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import alertMixin from 'shared/mixins/alertMixin';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import {
  ACTIVITY_AGENDA_PAGE_SIZE,
  ACTIVITY_AGENDA_REFRESH_DELAY,
  agendaReloadSize,
  reconnectRefreshDelay,
} from 'dashboard/helper/activityAgendaHelper';
import emptyAgendaImage from 'dashboard/assets/images/opportunity-pipelines/empty-activity-agenda.svg';
import OpportunityActivityModal from '../activities/OpportunityActivityModal.vue';
import ActivityAgendaItem from './ActivityAgendaItem.vue';

const NOW_REFRESH_INTERVAL = 60000;

const RECONCILE_INTERVAL = 5 * 60000;

const REFRESH_EVENTS = [
  'opportunity_activities_changed',
  'opportunity_updated',
  'opportunity_created',
];

export default {
  name: 'ActivityAgendaView',
  components: {
    ActivityAgendaItem,
    OpportunityActivityModal,
  },
  mixins: [alertMixin, opportunityWritePermission],
  data() {
    return {
      activities: [],
      totalCount: 0,
      loadedPages: 0,
      isLoading: false,
      isLoadingMore: false,
      hasError: false,
      busyActivityId: null,
      reschedulingActivity: null,
      isSubmitting: false,
      now: new Date(),
      nowTimer: null,
      requestId: 0,
      emptyImage: emptyAgendaImage,
    };
  },
  computed: {
    ...mapGetters({ summary: 'activityAgenda/getSummary' }),
    hasMore() {
      return this.activities.length < this.totalCount;
    },
  },
  created() {
    this.pendingReload = null;
    this.reconnectTimer = null;
    this.lastSyncedAt = null;
    this.scheduleReload = debounce(
      () => this.reload(),
      ACTIVITY_AGENDA_REFRESH_DELAY
    );
  },
  mounted() {
    this.fetchFirstPage();
    REFRESH_EVENTS.forEach(event => window.bus.$on(event, this.onChange));
    window.bus.$on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.nowTimer = window.setInterval(this.tick, NOW_REFRESH_INTERVAL);
  },
  beforeDestroy() {
    REFRESH_EVENTS.forEach(event => window.bus.$off(event, this.onChange));
    window.bus.$off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.clearInterval(this.nowTimer);
    window.clearTimeout(this.reconnectTimer);
  },
  methods: {
    nextRequestId() {
      this.requestId += 1;
      return this.requestId;
    },
    async fetchFirstPage() {
      const requestId = this.nextRequestId();
      this.isLoading = true;
      this.hasError = false;
      try {
        const { meta, payload } = await this.$store.dispatch(
          'activityAgenda/list',
          {
            page: 1,
            perPage: ACTIVITY_AGENDA_PAGE_SIZE,
          }
        );
        if (requestId !== this.requestId) return;
        this.applyList(payload, meta, 1);
      } catch (error) {
        if (requestId === this.requestId) this.hasError = true;
      } finally {
        if (requestId === this.requestId) this.isLoading = false;
      }
    },
    async loadMore() {
      if (this.isLoadingMore || !this.hasMore) return;

      this.isLoadingMore = true;
      let listVersion = this.requestId;
      try {
        await this.pendingReload;
        if (!this.hasMore) return;

        listVersion = this.requestId;
        const page = this.loadedPages + 1;
        const { meta, payload } = await this.$store.dispatch(
          'activityAgenda/list',
          {
            page,
            perPage: ACTIVITY_AGENDA_PAGE_SIZE,
          }
        );
        if (listVersion !== this.requestId) return;
        const knownIds = new Set(this.activities.map(({ id }) => id));
        this.activities = this.activities.concat(
          (payload || []).filter(({ id }) => !knownIds.has(id))
        );
        this.totalCount = meta.count;
        this.loadedPages = page;
      } catch (error) {
        if (listVersion === this.requestId) {
          this.showAlert(this.$t('ACTIVITY_AGENDA.ERRORS.LOAD'));
        }
      } finally {
        this.isLoadingMore = false;
      }
    },
    reload() {
      const pending = this.fetchReload();
      this.pendingReload = pending;
      return pending.finally(() => {
        if (this.pendingReload === pending) this.pendingReload = null;
      });
    },
    async fetchReload() {
      const requestId = this.nextRequestId();
      const perPage = agendaReloadSize(this.activities.length);
      try {
        const { meta, payload } = await this.$store.dispatch(
          'activityAgenda/list',
          {
            page: 1,
            perPage,
          }
        );
        if (requestId !== this.requestId) return;
        this.applyList(payload, meta, perPage / ACTIVITY_AGENDA_PAGE_SIZE);
      } catch (error) {
        if (requestId !== this.requestId) return;
        if (this.isLoading) {
          this.hasError = true;
          this.isLoading = false;
        } else {
          this.showAlert(this.$t('ACTIVITY_AGENDA.ERRORS.LOAD'));
        }
      }
    },
    applyList(payload, meta, loadedPages) {
      this.activities = payload || [];
      this.totalCount = meta.count;
      this.loadedPages = loadedPages;
      this.hasError = false;
      this.isLoading = false;
      this.now = new Date();
      this.lastSyncedAt = this.now.getTime();
    },
    onChange() {
      this.scheduleReload();
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.onChange();
    },
    onReconnect() {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = window.setTimeout(
        this.onChange,
        reconnectRefreshDelay()
      );
    },
    tick() {
      const previous = this.now;
      this.now = new Date();
      if (
        previous.toDateString() !== this.now.toDateString() ||
        this.isReconcileDue()
      ) {
        this.reload();
        return;
      }
      if (this.hasDeadlinePassed()) {
        this.$store.dispatch('activityAgenda/fetchSummary').catch(() => null);
      }
    },
    isReconcileDue() {
      return (
        document.visibilityState !== 'hidden' &&
        !!this.lastSyncedAt &&
        this.now.getTime() - this.lastSyncedAt >= RECONCILE_INTERVAL
      );
    },
    hasDeadlinePassed() {
      const { nextOverdueAt } = this.summary;
      return !!nextOverdueAt && new Date(nextOverdueAt) < this.now;
    },
    async changeStatus(activity, status, successKey, errorKey) {
      if (!this.canWriteOpportunities || this.busyActivityId) return;

      this.busyActivityId = activity.id;
      try {
        await this.$store.dispatch('opportunities/updateActivityStatus', {
          opportunityId: activity.opportunity_id,
          activityId: activity.id,
          status,
        });
        this.showAlert(this.$t(successKey));
      } catch (error) {
        this.showAlert(parseAPIErrorResponse(error) || this.$t(errorKey));
      } finally {
        this.busyActivityId = null;
      }
      await this.reload();
    },
    completeActivity(activity) {
      return this.changeStatus(
        activity,
        'completed',
        'OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.COMPLETED',
        'OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.COMPLETE'
      );
    },
    async cancelActivity(activity) {
      if (!this.canWriteOpportunities || this.busyActivityId) return;

      const confirmed = await this.$refs.cancelConfirmation.showConfirmation();
      if (!confirmed) return;

      await this.changeStatus(
        activity,
        'canceled',
        'OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.CANCELED',
        'OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.CANCEL'
      );
    },
    openRescheduleModal(activity) {
      if (!this.canWriteOpportunities) return;
      this.reschedulingActivity = activity;
    },
    closeRescheduleModal() {
      this.reschedulingActivity = null;
    },
    async rescheduleActivity(form) {
      const activity = this.reschedulingActivity;
      if (!activity || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        await this.$store.dispatch('opportunities/rescheduleActivity', {
          opportunityId: activity.opportunity_id,
          activityId: activity.id,
          scheduledAt: form.scheduledAt.toISOString(),
        });
        this.closeRescheduleModal();
        this.showAlert(
          this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.RESCHEDULED')
        );
        await this.reload();
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.RESCHEDULE')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-agenda {
  background: var(--s-25);
  height: 100%;
  overflow-y: auto;
  padding: var(--space-large) var(--space-larger);
  width: 100%;
}

.activity-agenda__summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-medium);
  margin-bottom: var(--space-large);
}

.agenda-summary-card {
  align-items: center;
  background: var(--white);
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-medium);
  display: flex;
  gap: var(--space-normal);
  min-width: 28rem;
  padding: var(--space-normal);
}

.agenda-summary-card__icon {
  align-items: center;
  border-radius: var(--border-radius-medium);
  display: inline-flex;
  flex-shrink: 0;
  height: 6rem;
  justify-content: center;
  width: 6rem;
}

.agenda-summary-card__icon--today {
  background: var(--w-50);
  color: var(--w-900);
}

.agenda-summary-card__icon--overdue {
  background: var(--r-100);
  color: var(--r-700);
}

.agenda-summary-card__label {
  color: var(--s-900);
  font-size: var(--font-size-default);
  margin: 0;
}

.agenda-summary-card__count {
  color: var(--s-900);
  font-size: var(--font-size-big);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.activity-agenda__read-only {
  background: var(--s-50);
  border: 1px dashed var(--s-200);
  border-radius: var(--border-radius-small);
  color: var(--s-700);
  font-size: var(--font-size-small);
  margin-bottom: var(--space-normal);
  max-width: 120rem;
  padding: var(--space-small) var(--space-normal);
  text-align: center;
}

.activity-agenda__error {
  color: var(--r-500);
  font-size: var(--font-size-small);
  padding: var(--space-large) 0;
  text-align: center;
}

.activity-agenda__content {
  max-width: 120rem;
}

.activity-agenda__list {
  margin: 0;
  padding: 0;
}

.activity-agenda__load-more {
  display: flex;
  margin: 0 auto var(--space-large);
}

.activity-agenda__empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: var(--space-larger) 0;
  text-align: center;
}

.activity-agenda__empty-image {
  max-width: 48rem;
  width: 80%;
}

.activity-agenda__empty-message {
  color: var(--s-900);
  font-size: var(--font-size-default);
  margin: var(--space-normal) 0 0;
}
</style>
